import { useMutation, useQuery } from '@apollo/client';
import Address from '@website/components/forms/fields/Address';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import useGeolocation from '@website/hooks/geoLocation/useGeolocation';
import {
  CountryCodes,
  GetHostBusinessDetailsByUrlDocument,
  GetHostBusinessDetailsByUrlQuery,
  HostBusinessInfoInput,
  UpdateHostBusinessDetailsDocument,
} from '@website/types/graphqlOperations';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { Alert, Breadcrumb, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

interface HostBusinessDetailsFormProps {
  urlSlug: string;
  data: GetHostBusinessDetailsByUrlQuery['hostByURL'];
}

export function HostBusinessDetailsForm({
  data,
  urlSlug,
}: HostBusinessDetailsFormProps) {
  const geoLocation = useGeolocation();

  const errorMessageRef = useRef<HTMLInputElement>(null);

  const [updateHostDetails, { loading: isMutationLoading, error }] =
    useMutation(UpdateHostBusinessDetailsDocument);

  const { businessAddress, legalName, companyRegistrationNumber } = data ?? {};
  const { country } = businessAddress ?? {};

  const methods = useForm<HostBusinessInfoInput>({
    mode: 'onBlur',
    defaultValues: {
      businessAddress: {
        ...businessAddress,
        // if form has value use the value, otherwise use the default
        country: country || geoLocation?.country?.code || CountryCodes.GB,
      },
      businessName: legalName,
      companyRegistrationNumber,
    },
  });

  useEffect(() => {
    // scroll to the point where error message is
    if (error && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView();
    }
  }, [error]);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = methods;

  const submitHostProfileForm = async (formData: HostBusinessInfoInput) => {
    await updateHostDetails({
      variables: {
        urlSlug,
        businessInfo: formData,
      },
    });
    // redirect on success
    // Router.push(`/hosts/dashboard/${urlSlug}`);
  };

  return (
    <>
      <Breadcrumb className="py-3">
        <Link href={`/hosts/dashboard/${urlSlug}`} passHref legacyBehavior>
          <Breadcrumb.Item href="">Host Dashboard</Breadcrumb.Item>
        </Link>
        <Breadcrumb.Item>Edit</Breadcrumb.Item>
        <Breadcrumb.Item active>Business Details</Breadcrumb.Item>
      </Breadcrumb>
      <h3 className="h3">Update Host Business Details</h3>
      {error && (
        <Alert ref={errorMessageRef} variant="warning">
          <Alert.Heading> Something went wrong</Alert.Heading>
          {JSON.stringify(error)}
        </Alert>
      )}
      <Form role="form" onSubmit={handleSubmit(submitHostProfileForm)}>
        <Form.Group controlId="name">
          <Form.Label>Legal Business Name *</Form.Label>
          <Form.Control
            type="text"
            required
            placeholder="Legal Business Name"
            {...register('businessName', { required: true })}
            isInvalid={!!errors.businessName}
          />
        </Form.Group>

        <Form.Group controlId="companyRegistrationNumber">
          <Form.Label>Company Registration Number *</Form.Label>
          <Form.Control
            type="text"
            required
            placeholder="Company Registration Number"
            {...register('companyRegistrationNumber', { required: true })}
            isInvalid={!!errors.companyRegistrationNumber}
          />
        </Form.Group>

        <Address
          watch={watch}
          register={register}
          name="businessAddress"
          localityRequired
        />

        <Form.Text className="text-muted">
          The fields marked with an <strong>asterisk *</strong> are required.
        </Form.Text>
        <SubmitButton
          text="Save"
          submittingLabel="Saving"
          isSubmitting={isMutationLoading}
        />
      </Form>
    </>
  );
}

interface HostBusinessDetailsFormWrapperProps {
  urlSlug: string;
}

export default function HostBusinessDetailsFormWrapper({
  urlSlug,
}: HostBusinessDetailsFormWrapperProps) {
  const { loading, error, data } = useQuery(
    GetHostBusinessDetailsByUrlDocument,
    {
      variables: {
        urlSlug,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return <HostBusinessDetailsForm urlSlug={urlSlug} data={data?.hostByURL} />;
}
